import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

// ─── Lookup data for the deliveries filter ────────────────────────────
// Recipient options come from the client's outlets; Mitra options from the
// driver directory. Both support server-side `search`.
const CHANNEL_HEADERS_LIVE = {
  'X-Channel': 'LINK',
  'X-Api-Mode': 'live',
} as const;

export interface LookupOption {
  value: string;
  label: string;
}

interface OutletRaw {
  id: string;
  name: string;
}

interface DriverRaw {
  id: string | number;
  code?: string;
  first_name?: string;
  last_name?: string;
}

interface LookupParams {
  page?: number;
  size?: number;
  search?: string;
}

// GET /client-user/v1/clients/me/outlets — the client's own outlets (Recipient).
export const getOutletOptions = async (
  params: LookupParams = {}
): Promise<LookupOption[]> => {
  try {
    const response = await Axios.get<{ data: OutletRaw[] }>(
      '/client-user/v1/clients/me/outlets',
      {
        headers: CHANNEL_HEADERS_LIVE,
        params: {
          page: params.page ?? 1,
          size: params.size ?? 999,
          ...(params.search ? { search: params.search } : {}),
        },
      }
    );
    return (response.data.data ?? []).map((o) => ({
      value: o.id,
      label: o.name,
    }));
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

// GET /v3/drivers — driver directory (Mitra).
export const getDriverOptions = async (
  params: LookupParams = {}
): Promise<LookupOption[]> => {
  try {
    const response = await Axios.get<{ data: DriverRaw[] }>('/v3/drivers', {
      headers: CHANNEL_HEADERS_LIVE,
      params: {
        page: params.page ?? 1,
        size: params.size ?? 50,
        sort: 'created_at',
        order: 'desc',
        ...(params.search ? { search: params.search } : {}),
      },
    });
    return (response.data.data ?? []).map((d) => ({
      value: String(d.id),
      label: [d.code, d.first_name].filter(Boolean).join(' - ') || String(d.id),
    }));
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};
